import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Mountain,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Malaysia DMC", path: "/malaysia-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE & Conferences",
    copy:
      "Full event production at Kuala Lumpur Convention Centre, MITEC, Sunway Resort and Penang's SPICE Arena — board offsites to multi-day congresses.",
  },
  {
    icon: Briefcase,
    title: "Corporate Group Travel",
    copy:
      "End-to-end programme delivery — flights, hotel blocks, transfers, F&B and 24/7 ground operations across KL, Penang and Langkawi.",
  },
  {
    icon: Trophy,
    title: "Incentive Travel",
    copy:
      "Beach retreats at The Datai Langkawi, heritage trails in George Town, themed gala dinners at the Petronas Twin Towers KLCC park.",
  },
  {
    icon: Mountain,
    title: "Leisure & Multi-City",
    copy:
      "Multi-city Malaysia itineraries — KL city to Penang heritage to Langkawi beach — wrapped with private transfers, English-speaking guides and hand-picked stays.",
  },
];

const CITIES = [
  {
    name: "Kuala Lumpur",
    note:
      "The MICE engine of Malaysia. KLCC, Mandarin Oriental and Grand Hyatt anchor a CBD packed with 5-star inventory. Excellent for conferences of 300–3,000 pax with budget-friendly rates compared to Singapore.",
  },
  {
    name: "Penang (George Town)",
    note:
      "UNESCO-listed heritage city perfect for incentive groups. Boutique hotels, eclectic gastronomy and street art tours create photogenic moments your delegates will share. Direct flights from KL in 50 minutes.",
  },
  {
    name: "Langkawi",
    note:
      "Tax-free island for top-tier incentive retreats. The Datai, Four Seasons and Ritz-Carlton offer luxury beachfront buyouts. Helicopter transfers and yacht charters complete the picture.",
  },
];

const VENUES = [
  { name: "Kuala Lumpur Convention Centre (KLCC)", note: "Flagship venue beside the Petronas Twin Towers" },
  { name: "Malaysia International Trade & Exhibition Centre (MITEC)", note: "Largest exhibition hall in Southeast Asia at 45,000 sqm" },
  { name: "Sunway Resort Hotel & Spa", note: "Integrated resort with theme park, 1,200+ rooms" },
  { name: "SPICE Arena Penang", note: "13,000 sqm convention space — Penang's largest" },
  { name: "Mandarin Oriental Kuala Lumpur", note: "Premium ballrooms for high-end corporate galas" },
  { name: "The Datai Langkawi", note: "Luxury rainforest resort — top choice for executive incentive buyouts" },
];

const FAQS = [
  {
    q: "Why choose Malaysia over Singapore or Thailand for corporate groups?",
    a: "Malaysia offers 30–40% better value than Singapore for comparable 5-star inventory, while maintaining strong English-speaking talent, modern infrastructure and the ease of a low-friction visa regime. For corporate groups looking to stretch budgets without compromising on quality, Malaysia is consistently our top recommendation.",
  },
  {
    q: "What is the best Malaysian destination for an incentive programme?",
    a: "It depends on the storyline. For luxury beach incentives, Langkawi is unmatched — The Datai and Four Seasons offer world-class properties at competitive rates. For culture and gastronomy programmes, Penang's UNESCO heritage zone delivers photogenic, unique experiences. For mixed business and leisure, a Kuala Lumpur to Langkawi combination works beautifully.",
  },
  {
    q: "Does Trawelldmc handle multi-city Malaysia itineraries?",
    a: "Yes. Multi-city programmes — KL to Penang to Langkawi, for example — are a regular part of our work. We coordinate internal flights or coach transfers, manage all hotel hand-offs, brief local English-speaking guides at each location, and assign one named programme manager across the entire trip for seamless continuity.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function MalaysiaDmc() {
  return (
    <div data-testid="malaysia-dmc-page">
      <Seo
        path="/malaysia-dmc"
        title="DMC Malaysia — Destination Management Company | Trawelldmc"
        description="Expert DMC in Malaysia — Kuala Lumpur, Penang & Langkawi. Corporate travel, MICE, and incentive programmes by Trawelldmc. 20+ years experience."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.malaysia}
          alt="Kuala Lumpur corporate travel — Trawelldmc DMC Malaysia"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Malaysia DMC · KL · Penang · Langkawi
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Malaysia</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              Value-led MICE, incentive and corporate travel across Malaysia's
              best cities and resorts — managed by a team with two decades on
              the ground.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquiry?destination=Malaysia"
                className="btn-primary"
                data-testid="my-cta-proposal"
              >
                Get a Malaysia proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/quote"
                className="btn-secondary"
                data-testid="my-cta-ai-quote"
              >
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY MALAYSIA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Malaysia</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Three destinations. <br />One country.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Malaysia is Southeast Asia's most under-priced premium MICE
              destination. For 30–40% less than Singapore — and with comparable
              5-star inventory, modern convention infrastructure and a strong
              English-speaking workforce — Malaysia consistently delivers
              programmes that look more expensive than they cost. For corporate
              buyers under budget pressure, that gap matters.
            </p>
            <p>
              Trawelldmc operates Malaysia as three connected destinations.
              Kuala Lumpur is the MICE engine: the Kuala Lumpur Convention
              Centre (KLCC) and the Malaysia International Trade & Exhibition
              Centre (MITEC) handle congresses of any size, surrounded by
              international hotel brands like Mandarin Oriental, Grand Hyatt,
              St. Regis and Four Seasons. Penang's George Town brings UNESCO
              heritage, an iconic street-food scene and boutique stays perfect
              for creative incentive narratives. Langkawi is the trump card —
              a duty-free island where The Datai, Four Seasons and Ritz-Carlton
              compete to host your top performers.
            </p>
            <p>
              We've delivered Malaysia for two decades. Our local team owns
              every contract, every transfer, every venue inspection. Visa,
              ground transport, F&B, AV, entertainment, branding — we don't
              outsource the parts that matter. We just deliver.
            </p>
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Where we work</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Our Malaysia footprint.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CITIES.map((c) => (
              <div key={c.name} className="card-flat p-8">
                <MapPin className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                  {c.name}
                </h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we deliver</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Our Malaysia DMC services.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8">
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                {s.title}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VENUES */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Hotels & venues <br />
              <span className="text-[#FF5238]">we partner with.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">
                      {v.name}
                    </div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`my-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">
                {f.q}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Ready to plan your <br />
              <span className="text-[#FF5238]">Malaysia programme?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Contact Trawelldmc for a custom Malaysia proposal. We respond
              within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/inquiry?destination=Malaysia"
              className="btn-primary w-full justify-center"
              data-testid="my-bottom-cta"
            >
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="btn-secondary w-full justify-center"
            >
              Contact our team
            </Link>
          </div>
        </div>
      </section>

      {/* YOU MAY ALSO LIKE */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            aria-label="Related DMC destination pages"
          >
            <Link to="/singapore-dmc" className="group flex items-start gap-3" data-testid="my-link-singapore">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Singapore</div>
                <div className="text-sm text-slate-500 mt-1">MICE capital of Asia</div>
              </div>
            </Link>
            <Link to="/thailand-dmc" className="group flex items-start gap-3" data-testid="my-link-thailand">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Thailand</div>
                <div className="text-sm text-slate-500 mt-1">Bangkok · Phuket · Chiang Mai</div>
              </div>
            </Link>
            <Link to="/dubai-dmc" className="group flex items-start gap-3" data-testid="my-link-dubai">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Dubai</div>
                <div className="text-sm text-slate-500 mt-1">GCC HQ · MICE & corporate</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="my-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
